import { useEffect, useState } from 'react'
import { Dialog } from '../ui/Dialog'
import { DestinationPicker } from '../ui/DestinationPicker'
import { useCommand } from '../state/useCommand'
import { useToast } from '../ui/Toast'
import { TelephonyCommandNames } from '../contract'
import type { VmMessage } from '../realtime/voicemail'
import { prettyNumber } from './_shared'
import { IconPlayerPlay, IconMailOpened, IconArrowForward, IconTrash } from '../ui/icons'

const fmtDur = (s: number) => (s < 60 ? `${s}s` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`)
const fmtAgo = (iso: string) => {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000))
  if (mins < 60) return `${mins}m ago`
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`
  return `${Math.round(mins / 1440)}d ago`
}

export function VoicemailMessagesDialog({ mailbox, onClose }: { mailbox: string; onClose: () => void }) {
  const run = useCommand()
  const { push } = useToast()
  const [messages, setMessages] = useState<VmMessage[] | null>(null)
  const [unavailable, setUnavailable] = useState(false)
  const [forwardMsg, setForwardMsg] = useState<VmMessage | null>(null)
  const [forwardTo, setForwardTo] = useState('')

  useEffect(() => {
    let alive = true
    void (async () => {
      const res = await run({ command: TelephonyCommandNames.VoicemailList, params: { mailbox } })
      if (!alive) return
      if (res && res.status === 'success') {
        setMessages((res.result as { messages?: VmMessage[] }).messages ?? [])
      } else {
        setUnavailable(true)
        setMessages([])
      }
    })()
    return () => { alive = false }
  }, [run, mailbox])

  const markRead = async (m: VmMessage) => {
    const res = await run(
      { command: TelephonyCommandNames.VoicemailMarkRead, params: { mailbox, message_id: m.message_id } },
      { successMessage: 'Marked as read' }
    )
    if (res && res.status === 'success') {
      setMessages((xs) => (xs ?? []).map((x) => (x.message_id === m.message_id ? { ...x, folder: 'old' } : x)))
    }
  }
  const del = async (m: VmMessage) => {
    const res = await run(
      { command: TelephonyCommandNames.VoicemailDelete, params: { mailbox, message_id: m.message_id } },
      { successMessage: 'Voicemail deleted' }
    )
    if (res && res.status === 'success') {
      setMessages((xs) => (xs ?? []).filter((x) => x.message_id !== m.message_id))
    }
  }
  const submitForward = async () => {
    if (!forwardMsg || !forwardTo.trim()) return
    const res = await run(
      { command: TelephonyCommandNames.VoicemailForward, params: { mailbox, message_id: forwardMsg.message_id, to_mailbox: forwardTo.trim() } },
      { successMessage: `Forwarded to ${forwardTo.trim()}` }
    )
    if (res && res.status === 'success') { setForwardMsg(null); setForwardTo('') }
  }

  return (
    <Dialog title={`Voicemail — mailbox ${mailbox}`} onClose={onClose}>
      {messages === null ? (
        <div className="sb-panel-empty">Loading messages…</div>
      ) : messages.length === 0 ? (
        <div className="sb-panel-empty">
          {unavailable ? 'Message list is pending the backend vm_list handler.' : 'No messages.'}
        </div>
      ) : (
        messages.map((m) => (
          <div key={m.message_id} className="sb-vm-msg">
            <span className={m.folder === 'new' ? 'sb-vm-new' : 'sb-vm-new read'} title={m.folder} />
            <div className="sb-vm-meta">
              <div className="sb-vm-from">{m.from_name || prettyNumber(m.from_number) || 'Unknown'}</div>
              <div className="sb-vm-sub">{prettyNumber(m.from_number)} · {fmtAgo(m.received_at)} · {fmtDur(m.duration_seconds)}</div>
            </div>
            <div className="sb-vm-acts">
              <button className="sb-iconbtn" title="Play (needs backend SAS)" aria-label="Play"
                onClick={() => push('Voicemail playback needs the file-ready/SAS path from the backend (pending).', 'info')}>
                <IconPlayerPlay size={16} stroke={1.75} />
              </button>
              {m.folder === 'new' ? (
                <button className="sb-iconbtn" title="Mark read" aria-label="Mark read" onClick={() => void markRead(m)}>
                  <IconMailOpened size={16} stroke={1.75} />
                </button>
              ) : null}
              <button className="sb-iconbtn" title="Forward" aria-label="Forward" onClick={() => { setForwardMsg(m); setForwardTo('') }}>
                <IconArrowForward size={16} stroke={1.75} />
              </button>
              <button className="sb-iconbtn danger" title="Delete" aria-label="Delete" onClick={() => void del(m)}>
                <IconTrash size={16} stroke={1.75} />
              </button>
            </div>
          </div>
        ))
      )}

      {forwardMsg ? (
        <Dialog
          title="Forward voicemail"
          onClose={() => setForwardMsg(null)}
          footer={
            <>
              <button className="sb-btn-ghost" onClick={() => setForwardMsg(null)}>Cancel</button>
              <button className="sb-btn-primary" disabled={!forwardTo.trim()} onClick={() => void submitForward()}>Forward</button>
            </>
          }
        >
          <DestinationPicker value={forwardTo} onChange={setForwardTo} label="Forward to mailbox" />
        </Dialog>
      ) : null}
    </Dialog>
  )
}
